import { IPromocode, checkPromocode } from "@/api/promocodes/promocodes";
import {
  AccountingState,
  PaymentMethods,
  PromocodeCampaignType,
  TransactionTypes,
} from "@/types/db/accounting";
import {
  IProductSlotState,
  PaymentStatus,
} from "@/types/db/product-slot-state";
import { getpssExtension } from "./booking";
import { getPSSWithToken } from "@/api/gql/product-slot-state";
import { getBookingInfo } from "@/api/gql/product-slot-boooking";
import BookingModule from "@/store/modules/booking";
import { LogLevel, LogService } from "@/services/logging";
import { PSBErrors } from "@/constants/errors/product-slot-booking";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
import CustomersModule from "@/store/modules/customers";
import POSConfigModule from "@/store/modules/pos-config";
import NotificationService from "@/services/notification.service";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { PromocodeErrors } from "@/constants/errors/promocode";
import { Weekday } from "@/constants/dates";
import { dateTimeToWeekDay } from "../helpers/dates";
import { cloneDeep } from "lodash";
import {
  createDiscountCodeAccounting,
  createPaymentCodeAccounting,
  getSumOfCodeGeneratedAccountingsValues,
  hasAppliedGenericDiscountTracking,
  removeAllDiscountCodePendingAccountings,
  removeAllPaymentCodePendingAccountings,
  removePaymentCodePendingAccountings,
  restToPay,
  updateGenericDiscountAccountings,
} from "./new-accountings";
import { sumArray } from "../helpers/common";
import { getPromocodeCampaginsByConfigId } from "@/api/gql/promocode-campaign";
import { IPromocodeCampaign } from "@/types/db/promocode-campaign";
import { getSmeetzPictures } from "@/api/gql/common";
import { PCTypes } from "@/types/db/product-category";
import { dexieDB } from "@/services/dexie-database";
import { atomicTableSwap } from "@/dexie/helpers";
import {
  createCroppedImageproxyUrl,
  SmeetzPictureEntityTables,
  SmeetzPictureEntityTypes,
} from "../helpers/pictures";
import dayjs from "dayjs";
import { applyOffersToBooking } from "../helpers/offers";
import OffersModule from "@/store/modules/offers";
import { Payment } from "@/components/index";

/**
 * Takes promocode info and a pss
 * Returns whether promocode will be applied on the pss or not
 * // TODO Hamza: notice here we need to write a new function getTicketGroupsbyCategoryId which
 * will be asynch and cannot be called from promocode discount calculator getter
 */
export function isToApply(promocode: IPromocode, pss: IProductSlotState) {
  const CONNECTION_TYPE_ALL = 1;
  const CONNECTION_TYPE_TICKET_GROUP = 2;
  const CONNECTION_TYPE_TICKET = 3;
  const CONNECTION_TYPE_OLD = 5;

  const promocodeProductId = promocode.product;
  const promocodePricesIds = promocode.prices;
  const promocodeConnectionType = promocode.connectionType;
  const promocodeTicketId = promocode.ticket;
  const promocodeticketGroupId = promocode.ticketGroup;
  const promoCodeType = promocode.promoCodeType;

  // If the pss PSC is of type TYPE_GIFT_CARD, then we can onyl apply codes of type TYPE_PROMO_CODE
  if (pss.type === PCTypes.GiftCard) {
    // We only let codes of type TYPE_PROMO_CODE gets applied on this pss
    if (promocode.promoCodeType !== PromocodeCampaignType.TYPE_PROMO_CODE) {
      return false;
    }
  }

  // When we use a membership, we can not get gift card, or all kind of memberships
  if (
    (pss.type === PCTypes.Membership ||
      pss.type === PCTypes.GiftCard ||
      pss.type === PCTypes.MembershipSeated ||
      pss.type === PCTypes.MembershipVoucher) &&
    (promocode.promoCodeType === PromocodeCampaignType.TYPE_MEMBERSHIP ||
      promocode.promoCodeType === PromocodeCampaignType.TYPE_MEMBERSHIP_SOLD)
  ) {
    return false;
  }

  //not allowed to apply membership/voucher on retail
  if (
    pss.type === PCTypes.Retail &&
    promocode.promoCodeType === PromocodeCampaignType.TYPE_MEMBERSHIP_SOLD
  ) {
    return false;
  }

  if (promocode.connectionType === CONNECTION_TYPE_ALL) {
    // groupId should match
    if (promocode.groupId === pss.productOwnerGroupId) {
      return true;
    }
  }
  const pssExt = getpssExtension(String(pss.uuid));
  if (
    promocodeConnectionType === CONNECTION_TYPE_OLD &&
    ((promocodePricesIds &&
      promocodePricesIds.length &&
      promocodePricesIds.findIndex((pr: any) => pr === pss.priceId) < 0) ||
      (promocodeProductId &&
        String(promocodeProductId) !== String(pss.productId)))
  ) {
    return false;
  } else if (
    promocodeConnectionType === CONNECTION_TYPE_TICKET &&
    promocodeTicketId !== pss.categoryId
  ) {
    return false;
  } else if (
    promocodeConnectionType === CONNECTION_TYPE_TICKET_GROUP &&
    promocodeticketGroupId &&
    pssExt &&
    pssExt.tickerGroups.findIndex((tg) => tg === promocodeticketGroupId) < 0
  ) {
    return false;
  }

  if (
    promoCodeType === PromocodeCampaignType.TYPE_TICKET_CODE ||
    promoCodeType === PromocodeCampaignType.TYPE_TICKET_CODE_SOLD
  ) {
    return false;
  }
  return true;
}

/**
 * This function extracts the voucher code from a url string
 *
 * Examples of possible url strings we get from the scanner:
 * https//appsmeetz.com/redeemvouchers?vc=UY4W2K3M3T
 * https//appsmeetzcom/redeemvouchersvc=UY4W2K3M3T
 * https//appsmeetzcom/redeemvouchersvcUY4W2K3M3T
 * https://app.smeetz.com/redeem-vouchers?vc=572ZS64YLH_old_236503
 *
 */
export function extractCodeFromUrl(value: string): string {
  const promoCodeMatch = value.match(/vc=?([A-Za-z0-9_-]+)/);
  if (promoCodeMatch) {
    return promoCodeMatch[1];
  }
  return "";
}

/**
 * Calls check-promocode api to check if promocode is valid
 * @param promocode
 * @param booking
 * @returns
 */
export async function checkPromocodeValidity(
  promocode: string,
  bookingUuid: string,
  pssList: IProductSlotState[]
) {
  // used in the check-promocode api call
  const pssInfoList: { timeSlotId: number | null; categoryId: number }[] = [];
  // used to check promocode valid days
  const pssStartDateTimesInfo: {
    pssUuid: string | null;
    startDateTime: string | undefined;
    priceName: string;
  }[] = [];
  const pricesIds: number[] = [];
  for (let i = 0; i < pssList.length; i++) {
    pricesIds.push(pssList[i].priceId);
    const index = pssInfoList.findIndex(
      (pss) =>
        pss.timeSlotId === pssList[i].timeSlotId &&
        pss.categoryId === pssList[i].categoryId
    );
    if (index > -1) {
      continue;
    }
    pssInfoList.push({
      timeSlotId: pssList[i].timeSlotId || null,
      categoryId: pssList[i].categoryId,
    });
    pssStartDateTimesInfo.push({
      pssUuid: pssList[i].uuid,
      startDateTime: pssList[i].startDateTime,
      priceName: pssList[i].priceName,
    });
  }
  LogService.info(
    `Checking promocode validity, booking: ${bookingUuid}, promocode: ${promocode},`
  );
  const response = await checkPromocode(
    promocode,
    pricesIds,
    Number(POSConfigModule.selectedConfig?.group),
    Number(POSConfigModule.selectedConfig?.id),
    String(bookingUuid),
    pssInfoList
  );

  return { response, pssStartDateTimesInfo };
}

export function getPromocodePaymentType(
  promocodeType: PromocodeCampaignType | undefined
): PaymentMethods | null {
  if (promocodeType === PromocodeCampaignType.TYPE_PROMO_CODE) {
    // console.log("returning POS_PROMO_CODE");
    return PaymentMethods.POS_PROMO_CODE;
  }
  if (
    promocodeType === PromocodeCampaignType.TYPE_GIFT_CARD_SOLD ||
    promocodeType === PromocodeCampaignType.TYPE_VOUCHER_SOLD
  ) {
    // console.log("returning POS_GIFT_CARD");
    return PaymentMethods.POS_GIFT_CARD;
  }
  if (promocodeType === PromocodeCampaignType.TYPE_MEMBERSHIP_SOLD) {
    // console.log("returning POS_MEMBERSHIP");
    return PaymentMethods.POS_MEMBERSHIP;
  }
  // console.log("returning null");
  return null;
}

export function getPromocodeInputPlaceholder(type: PaymentMethods): string {
  if (type === PaymentMethods.POS_GIFT_CARD) {
    return t("promocode.enter-gift-card");
  }
  if (type === PaymentMethods.POS_MEMBERSHIP) {
    return t("promocode.enter-membership");
  }
  return t("promocode.enter");
}

export function getPromocodeErrorMessage(
  error: PromocodeErrors,
  paymentMethod?: PaymentMethods
): string {
  const type =
    paymentMethod === PaymentMethods.POS_GIFT_CARD
      ? "gift-card"
      : paymentMethod === PaymentMethods.POS_MEMBERSHIP
      ? "membership"
      : "promo-code";

  return t(`promocode.${type}-${error}`);
}

export function getPromocodeSuccessMessage(
  promocodeType: PromocodeCampaignType
) {
  let message = t("promocode.promo-code-applied");
  switch (promocodeType) {
    case PromocodeCampaignType.TYPE_GIFT_CARD_SOLD:
      message = t("promocode.gift-card-applied");
      break;
    case PromocodeCampaignType.TYPE_VOUCHER_SOLD:
      message = t("promocode.voucher-applied");
      break;
    case PromocodeCampaignType.TYPE_MEMBERSHIP_SOLD:
      message = t("promocode.membership-applied");
  }
  return message;
}

/**
 * Auto-assign customer from membership's original booking.
 * if no customer is assigned yet, fetch the membership's
 * original booking and copy customer data onto the current order.
 */
async function assignCustomerFromMembership(
  membershipToken: string,
  bookingUuid: string,
  groupId: number
): Promise<void> {
  const membershipPSS = await getPSSWithToken(membershipToken, groupId);
  if (!membershipPSS?.BookingId) return;

  const originalBooking = await getBookingInfo(String(membershipPSS.BookingId));
  const customer = originalBooking?.CustomerRead;
  if (!customer) return;

  BookingModule.setCustomerData({
    id: customer.id ?? null,
    bookingUuid,
    email: customer.email ?? "",
    firstName: customer.firstName ?? "",
    lastName: customer.lastName ?? "",
  });

  await CustomersModule.setSelectedCustomers([
    {
      fullName: `${customer.firstName ?? ""} ${customer.lastName ?? ""}`.trim(),
      bookingUuid,
      email: customer.email ?? "",
      id: customer.id ?? null,
      phone: customer.phone ?? "",
      address: customer.address ?? "",
      city: customer.city ?? "",
      country: customer.country?.name ?? "",
      zipCode: customer.zipCode ?? "",
      birthDate: customer.birthDate ?? "",
      title: customer.title ?? "",
      allergies: customer.allergies ?? "",
      emailMarketingOrganiser: customer.emailMarketingOrganiser ?? "",
      company: customer.company ?? "",
    },
  ]);

  LogService.info(
    `Membership customer auto-assigned to booking: ${bookingUuid}`,
    { customerId: customer.id }
  );
}

/**
 * Check and apply promocode if valid
 */
export async function processPromocode(
  code: string,
  pssList?: IProductSlotState[]
) {
  // 0- Is user is just clicking again and again on the same code? or a promocode processing job is already being done? If no go to (2), If yes do nothing
  // 1- Check validaity/availability of the promocode from backend
  // 2- If not valid show a notif
  // 3- If valid Implement promocode logic

  const booking = BookingModule.selectedBooking;
  if (!booking) {
    throw new Error(PSBErrors.NoBookingInModule);
  }
  if (!booking.uuid) {
    throw new Error(PSBErrors.HasNoUUID);
  }

  if (BookingModule.promocodeBeingChecked) {
    // there is already a promocode check being done in the background, inform the user and return
    NotificationService.showCustomInfoWithTitle(
      t("promocode.already-being-checked"),
      t("promocode.please-wait")
    );
    return;
  }
  BookingModule.setPromocodeBeingChecked(true);

  let isSameCode = false;
  // we assign isSameCode value of true only if the user is applying again and again the same code
  // we know that the user has just applied this code when this code has a pending accounting created for it and it has accEntries
  const codeAccountings = BookingModule.selectedBooking?.accountings.filter(
    (acc) =>
      (acc.state === AccountingState.Pending ||
        (acc.state === AccountingState.Completed &&
          !acc.id)) /*recent code accounting that gets completed if a user applies a payment*/ &&
      !acc.refund &&
      (acc.promocode === code || acc.promoCodeUuid === code) &&
      acc.accountingEntriesList &&
      acc.accountingEntriesList.length
  );

  if (codeAccountings && codeAccountings.length) {
    isSameCode = true;
  }

  if (isSameCode) {
    // user is tring to apply the same code again, we do nothing
    LogService.info(
      `User is tring to apply the same code again ${code}, we do nothing, booking: ${BookingModule.selectedBookingUUID}`
    );
    BookingModule.setPromocodeBeingChecked(false);
    return;
  }

  try {
    // if pss list was not passed as an argument, use all pss of the current booking
    if (!pssList) pssList = booking.pssList;
    // 1- Get check promocode validity response
    const { response, pssStartDateTimesInfo } = await checkPromocodeValidity(
      code,
      booking.uuid,
      pssList
    );
    const promocodeData = response.data.promoCodeData;

    const promocodePaymentType = getPromocodePaymentType(
      promocodeData.promoCodeType
    );
    // If unknown promocode type
    if (!promocodePaymentType) {
      NotificationService.showCustomMessage(
        getPromocodeErrorMessage(PromocodeErrors.NOT_FOUND),
        "warning"
      );
      return;
    }

    // check if promocode is valid for all days of the week,
    // if it's not, compare its validity to pss.startDateTime
    promocodeData.validDays = [];
    if (!promocodeData.isValidAllDays) {
      // store valid days of promocode
      const possibleWeekdays: Weekday[] = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];
      possibleWeekdays.forEach((day) => {
        if (promocodeData[day] === true) {
          promocodeData.validDays.push(day);
        }
      });
      LogService.info(
        `Promocode is not valid for all days: ${code}, checking if it affects current pss list`
      );
      const pssWithInvalidStartingDay: {
        pssUuid: string | null;
        startDateTime: string | undefined;
        priceName: string;
      }[] = [];
      pssStartDateTimesInfo.forEach((pssInfo) => {
        if (!pssInfo.startDateTime) {
          // skip pss with no startDateTime
          return;
        }
        const startDateTimeWeekDay = dateTimeToWeekDay(pssInfo.startDateTime);
        if (!promocodeData[startDateTimeWeekDay]) {
          LogService.info(
            `Promocode cannot be applied to pss ${pssInfo.pssUuid} on ${startDateTimeWeekDay}`
          );
          pssWithInvalidStartingDay.push(pssInfo);
        }
      });

      if (pssWithInvalidStartingDay.length > 0) {
        // at least one pss cannot be discounted with the applied promocode, we notify the user
        const translatedPromocodeInvalidDays = possibleWeekdays
          .filter((day) => !promocodeData.validDays.includes(day))
          .map((invalidDay) => t(`date.${invalidDay}`))
          .join(", ");
        NotificationService.showCustomMessage(
          `${getPromocodeErrorMessage(
            PromocodeErrors.INVALID_DAYS
          )} ${translatedPromocodeInvalidDays}`,
          "warning"
        );
        if (pssWithInvalidStartingDay.length === pssStartDateTimesInfo.length) {
          // no pss can be discounted, we stop the flow here
          return;
        }
      }
    }

    // setSuccessMessage(promocodeData.promoCodeType);
    // updatePaymentType(promocodePaymentType);

    LogService.info(
      `Response received, promocode valid, booking: ${BookingModule.selectedBookingUUID}`,
      { params: response }
    );

    // 3- If valid, store the promocode response in BookingModule.promocodes array
    promocodeData.code = code;

    if (promocodeData) {
      let membershipToken = "";
      if (
        promocodeData.promoCodeType === PromocodeCampaignType.TYPE_MEMBERSHIP ||
        promocodeData.promoCodeType ===
          PromocodeCampaignType.TYPE_MEMBERSHIP_SOLD ||
        promocodeData.promoCodeType === PromocodeCampaignType.TYPE_GIFT_CARD ||
        promocodeData.promoCodeType ===
          PromocodeCampaignType.TYPE_GIFT_CARD_SOLD
      ) {
        membershipToken = promocodeData.membershipToken;

        const isMembership =
          promocodeData.promoCodeType ===
            PromocodeCampaignType.TYPE_MEMBERSHIP ||
          promocodeData.promoCodeType ===
            PromocodeCampaignType.TYPE_MEMBERSHIP_SOLD;

        if (isMembership && booking.uuid) {
          const hasCustomerAlready =
            CustomersModule.getSelectedCustomerId !== null;

          if (!hasCustomerAlready) {
            try {
              await assignCustomerFromMembership(
                membershipToken,
                booking.uuid,
                POSConfigModule.groupId as number
              );
            } catch (err) {
              LogService.info("Failed to auto-assign membership customer", {
                err,
              });
            }
          }
        }
      }

      BookingModule.addPromocode({
        promocode: promocodeData,
        bookingUuid: BookingModule.selectedBookingUUID,
        // if the code is a membership voucher, we store the membership's token (qrcode)
        // if the group has oneTokenVoucherTickets enabled, we set this token to the new tickets that this voucher is applied on
        token: membershipToken || "",
        pssUuids: pssList.map((pss) => pss.uuid as string),
        // in membership case, use offerIds, which contains ids of offers related to this membership
        // in case of promocodes use promocodeData.offerId if available
        // otherwise this field will be an empty array, in which case it isn't related to any offer
        offerIds: promocodeData.offersIds
          ? promocodeData.offersIds.ids
              .split(",")
              .map((id: string) => Number(id.trim()))
          : promocodeData.offerId
          ? [promocodeData.offerId]
          : [],
        membershipId: promocodeData.offersIds?.membershipId,
      });
      if (promocodeData.offerId || promocodeData.offersIds) {
        // if applied code is part of an offer, try to apply and exit
        await processOfferCode(promocodeData, code);
        return;
      }

      /////////////////////////// NEW ACCOUNTINGS LOGIC HERE
      if (
        promocodeData.promoCodeType !== PromocodeCampaignType.TYPE_VOUCHER &&
        promocodeData.promoCodeType !==
          PromocodeCampaignType.TYPE_VOUCHER_SOLD &&
        promocodeData.promoCodeType !== PromocodeCampaignType.TYPE_GIFT_CARD &&
        promocodeData.promoCodeType !==
          PromocodeCampaignType.TYPE_GIFT_CARD_SOLD
      ) {
        // So this is a discount code applied by the user, lets:
        //     - Remove pending payment code accountings of all applied payment codes
        //     - Remove pending discount code accountings of all applied discount codes
        //     - Create discount code accountings of all applied discount codes
        //     - Create payment code accountings of all applied payment codes
        const bookingCopy = cloneDeep(
          BookingModule.selectedBooking as IProductSlotBooking
        );
        // const promocodeTrack = BookingModule.appliedPromocodes.find(
        //   (p) => p.promocode.code === promocodeData.code
        // ) as IPromocodeTrack;
        // const discountValues = getPromocodeDiscountValue(
        //   bookingCopy,
        //   promocodeTrack
        // );
        //     - Remove pending payment code accountings of all applied payment codes
        // console.log("BEFORE Removing cloneDeep(bookingCopy)");
        // console.log(cloneDeep(bookingCopy));
        removeAllPaymentCodePendingAccountings(bookingCopy);
        //     - Remove pending discount code accountings of all applied discount codes
        removeAllDiscountCodePendingAccountings(bookingCopy);
        // console.log("AFTER Removing cloneDeep(bookingCopy)");
        // console.log(cloneDeep(bookingCopy));
        // remove the below to replicate creating accountings from selectedBooking instead of bookingCopy obj
        // BookingModule.updateBooking(bookingCopy);

        //     - Create discount code accountings of all applied discount codes
        // lets get all applied discount codes and sort them by applyDateTime
        // console.log("BEFORE cloneDeep(bookingCopy)");
        // console.log(cloneDeep(bookingCopy));
        const sortedAppliedDiscountCodes =
          BookingModule.appliedPromocodes.filter(
            (p) =>
              p.promocode.promoCodeType !==
                PromocodeCampaignType.TYPE_VOUCHER &&
              p.promocode.promoCodeType !==
                PromocodeCampaignType.TYPE_VOUCHER_SOLD &&
              p.promocode.promoCodeType !==
                PromocodeCampaignType.TYPE_GIFT_CARD &&
              p.promocode.promoCodeType !==
                PromocodeCampaignType.TYPE_GIFT_CARD_SOLD
          ); /*.sort((p) => p.createdAt)*/
        // console.log("sortedAppliedDiscountCodes");
        // console.log(sortedAppliedDiscountCodes);
        for (let i = 0; i < sortedAppliedDiscountCodes.length; i++) {
          // If the bookingCopy obj has already an accounting related to this discount code that is recently created (pending, or completed but has no id) then we don't create another accounting for the same code
          const recentCodeAccounting = bookingCopy.accountings.find(
            (acc) =>
              (acc.state === AccountingState.Pending ||
                (acc.state === AccountingState.Completed &&
                  !acc.id)) /*recent code accounting that gets completed if a user applies a payment*/ &&
              !acc.refund &&
              (acc.promocode === sortedAppliedDiscountCodes[i].promocode.code ||
                acc.promoCodeUuid ===
                  sortedAppliedDiscountCodes[i].promocode.code) &&
              acc.accountingEntriesList &&
              acc.accountingEntriesList.length
          );
          if (recentCodeAccounting) {
            // booking has already a recent accounting of this code, lets not create another one
            continue;
          }
          createDiscountCodeAccounting(
            bookingCopy,
            sortedAppliedDiscountCodes[i],
            bookingCopy.pssList.filter(
              (pss) =>
                pss.uuid &&
                sortedAppliedDiscountCodes[i].pssUuids.includes(pss.uuid)
            )
          );
          // console.log("after creating accounting of discount code: ", sortedAppliedDiscountCodes[i].promocode.code);
          // console.log(cloneDeep(bookingCopy));
        }
        // console.log("cloneDeep(bookingCopy)");
        // console.log(cloneDeep(bookingCopy));
        //     - Create payment code accountings of all applied payment codes
        // lets get all applied payment codes and sort them by applyDateTime
        const sortedAppliedPaymentCodes =
          BookingModule.appliedPromocodes.filter(
            (p) =>
              p.promocode.promoCodeType ===
                PromocodeCampaignType.TYPE_VOUCHER ||
              p.promocode.promoCodeType ===
                PromocodeCampaignType.TYPE_VOUCHER_SOLD ||
              p.promocode.promoCodeType ===
                PromocodeCampaignType.TYPE_GIFT_CARD ||
              p.promocode.promoCodeType ===
                PromocodeCampaignType.TYPE_GIFT_CARD_SOLD
          ); /*.sort((p) => p.createdAt)*/
        for (let i = 0; i < sortedAppliedPaymentCodes.length; i++) {
          createPaymentCodeAccounting(
            bookingCopy,
            sortedAppliedPaymentCodes[i],
            bookingCopy.pssList.filter(
              (pss) =>
                pss.uuid &&
                sortedAppliedDiscountCodes[i].pssUuids.includes(pss.uuid)
            )
          );
        }
        BookingModule.updateBooking(bookingCopy);
      } else {
        // this is a payment code applied by the user, lets:
        //     - Remove pending payment code accounting of applied payment code xyz in case there are any
        //     - Create payment code accounting of applied payment code xyz
        const bookingCopy = cloneDeep(
          BookingModule.selectedBooking as IProductSlotBooking
        );
        const promocodeTrack = BookingModule.appliedPromocodes.find(
          (p) => p.promocode.code === promocodeData.code
        );
        if (promocodeTrack) {
          removePaymentCodePendingAccountings(
            bookingCopy,
            promocodeTrack /*promocodeData*/
          );
          // remove the below to replicate creating accountings from selectedBooking instead of bookingCopy obj
          // BookingModule.updateBooking(bookingCopy);
          createPaymentCodeAccounting(
            bookingCopy,
            promocodeTrack /*promocodeData*/
          );
          BookingModule.updateBooking(bookingCopy);
        }
      }
      /////////////////////////// NEW ACCOUNTINGS LOGIC HERE

      //Update current amount that will be shown in cash payment UI
      BookingModule.addCurrentAmount({
        BookingUUID: BookingModule.selectedBookingUUID,
        amount: restToPay.value /*BookingModule.pssSumFinaValues*/ / 100,
      });
      BookingModule.setSplitPaymentAmount({
        BookingUUID: BookingModule.selectedBookingUUID,
        splitAmount: restToPay.value / 100 /*Math.min(
          BookingModule.pssSumFinaValues / 100,
          Math.abs(BookingModule.leftToPayAmount / 100)
        )*/,
      });
    }

    const sumOfCodeGeneratedAccountingsValues =
      getSumOfCodeGeneratedAccountingsValues(code);
    if (!sumOfCodeGeneratedAccountingsValues) {
      // The backend accepted the code, yet no pss line was eligible for a
      // discount or payment. The code is dropped without a specific error,
      // so log the eligibility inputs to make scope mismatches observable.
      LogService.info(
        `Promocode ${code} passed backend validation but generated no discount nor payment, booking: ${BookingModule.selectedBookingUUID}`,
        {
          params: {
            connectionType: promocodeData.connectionType,
            promoCodeType: promocodeData.promoCodeType,
            ticketGroup: promocodeData.ticketGroup,
            ticket: promocodeData.ticket,
            product: promocodeData.product,
            prices: promocodeData.prices,
            pssList: pssList.map((pss) => ({
              uuid: pss.uuid,
              categoryId: pss.categoryId,
              priceId: pss.priceId,
              tickerGroups: getpssExtension(String(pss.uuid))?.tickerGroups,
            })),
          },
        },
        LogLevel.Error
      );
      // so the code applied didn't generate a discount nor a payment
      BookingModule.removePromocode({
        bookingUuid: String(BookingModule.selectedBookingUUID),
        promocodeUuid: code,
      });
      NotificationService.showCustomMessage(
        getPromocodeErrorMessage(PromocodeErrors.NOT_VALID),
        "warning"
      );
    } else {
      NotificationService.showCustomMessage(
        getPromocodeSuccessMessage(promocodeData.promocodeType),
        "success"
      );
    }
  } catch (error: any) {
    // Show not valid notification for the user, log the error.
    // A request that never got a response (offline, timeout) carries no backend
    // messages, so msg stays empty and the generic message is shown.
    const data = error?.response?.data;
    const msg = Array.isArray(data?.messages) ? data.messages[0] : "";
    let translatedMsg = "";
    switch (msg) {
      // We don't know the type of the code we tried to apply, we only assumed that its type is what the user said
      // and in the error response we don't have the code type, so I think the assumption of the code type based on the user choice
      // can be misleading. Lets just use "Code not ..." nd their translations once supplied
      case "promocode not found":
        translatedMsg = getPromocodeErrorMessage(PromocodeErrors.NOT_FOUND);
        break;
      case "promocode is expired":
        translatedMsg = getPromocodeErrorMessage(PromocodeErrors.EXPIRED);
        break;
      case "promocode not available":
        translatedMsg = getPromocodeErrorMessage(PromocodeErrors.NOT_AVAILABLE);
        break;
      case "Promocode can not be used anymore":
        translatedMsg = getPromocodeErrorMessage(PromocodeErrors.NOT_AVAILABLE);
        break;
      default:
        translatedMsg = msg;
    }
    NotificationService.showCustomMessage(
      translatedMsg || t("error.general-error"),
      "warning"
    );
    LogService.info(
      `Error when checking promocode validity, booking: ${BookingModule.selectedBookingUUID}, promocode: ${code},`,
      error,
      LogLevel.Error
    );
  } finally {
    BookingModule.setPromocodeBeingChecked(false);
  }
}

export async function processOfferCode(promocodeData: any, code: string) {
  LogService.info(`Promocode is part of offer id ${promocodeData.offerId}`);
  await applyOffersToBooking(
    cloneDeep(BookingModule.selectedBooking as IProductSlotBooking)
  );
  const sumOfCodeGeneratedAccountingsValues =
    getSumOfCodeGeneratedAccountingsValues(code);
  if (sumOfCodeGeneratedAccountingsValues) {
    // offer and promocode were applied
    NotificationService.showCustomMessage(
      getPromocodeSuccessMessage(promocodeData.promocodeType),
      "success"
    );
    // update amount to pay
    BookingModule.addCurrentAmount({
      BookingUUID: BookingModule.selectedBookingUUID,
      amount: restToPay.value / 100,
    });
    BookingModule.setSplitPaymentAmount({
      BookingUUID: BookingModule.selectedBookingUUID,
      splitAmount: restToPay.value / 100,
    });
  } else {
    // so the code applied didn't generate a discount nor a payment
    BookingModule.removePromocode({
      bookingUuid: String(BookingModule.selectedBookingUUID),
      promocodeUuid: code,
    });
    NotificationService.showCustomMessage(
      getPromocodeErrorMessage(PromocodeErrors.NOT_VALID),
      "warning"
    );
  }
}

/**
 * Removes an accounting from the booking
 * The payment is related to an applied code
 * It reset paymentStatus of the pss linked to the removed payment
 */
export function removeCodePayment(appliedCode: string) {
  const booking = BookingModule.selectedBooking;
  if (!booking) return;
  const updatedBooking = cloneDeep(booking);
  const accountings = updatedBooking.accountings;
  const affectedPss = accountings
    .find(
      (acc) =>
        acc.state === AccountingState.Pending &&
        !acc.refund &&
        (acc.promocode === appliedCode || acc.promoCodeUuid === appliedCode)
    )
    ?.accountingEntriesList.map((ae) => ae.productSlotStateUuid);
  // remove the accounting
  let accountingToRemove = null;
  const index = accountings.findIndex(
    (acc) =>
      acc.state === AccountingState.Pending &&
      !acc.refund &&
      (acc.promocode === appliedCode || acc.promoCodeUuid === appliedCode)
  );
  if (index > -1) {
    // If Smeetz code then lets just make the accounting state Abondoned without removing it from the booking
    const smeetzCodesPaymentMethods: PaymentMethods[] = [
      PaymentMethods.POS_PROMO_CODE,
      PaymentMethods.POS_VOUCHER,
      PaymentMethods.POS_GIFT_CARD,
      PaymentMethods.POS_MEMBERSHIP,
      PaymentMethods.METHOD_PROMO_CODE,
      PaymentMethods.METHOD_VOUCHER,
      PaymentMethods.METHOD_GIFT_CARD,
      PaymentMethods.METHOD_MEMBERSHIP,
      PaymentMethods.METHOD_B2B_POS_PROMO_CODE,
      PaymentMethods.METHOD_B2B_POS_VOUCHER,
      PaymentMethods.METHOD_B2B_POS_GIFT_CARD,
      PaymentMethods.METHOD_B2B_POS_MEMBERSHIP,
    ];
    const isSmeetzCode =
      !accountings[index].isExternalVoucherService ||
      smeetzCodesPaymentMethods.includes(accountings[index].paymentMethod);
    if (isSmeetzCode) {
      accountings[index].state = AccountingState.Cancelled;
      // Now remove this exact promocode from BookingModule.promocodes
      BookingModule.removePromocode({
        bookingUuid: String(updatedBooking.uuid),
        promocodeUuid: appliedCode,
      });
    } else {
      // Keep everythingelse as it is
      accountingToRemove = cloneDeep(accountings[index]);
      accountings.splice(index, 1);
    }
  }
  // reset pss paymentStatus
  const pssList = updatedBooking.pssList;
  for (let i = 0; i < pssList.length; i++) {
    if (affectedPss?.includes(pssList[i].uuid)) {
      // So the removed payment is linked to this pss
      pssList[i].paymentStatus = PaymentStatus.NotStarted;

      // remove TICKET accounting of the pss if it has no payment
      const pssPaymentAccEntries = updatedBooking.accountings
        .filter(
          (acc) =>
            acc.transactionType === TransactionTypes.TRANSACTION_TYPE_PAYMENT &&
            acc.state === 5
        )
        .map((acc) => acc.accountingEntriesList)
        .flat()
        .filter((ae) => ae.productSlotStateUuid === pssList[i].uuid)
        .map((ae) => ae.value);
      const pssPayment = sumArray(pssPaymentAccEntries);
      if (!pssPayment) {
        const accountings = updatedBooking.accountings;
        const ind = accountings.findIndex(
          (a) =>
            a.transactionType === TransactionTypes.TRANSACTION_TYPE_TICKET &&
            a.state === AccountingState.Completed &&
            a.refund !== 1 &&
            a.accountingEntriesList
              .map((ae) => ae.productSlotStateUuid)
              .includes(pssList[i].uuid)
        );
        if (ind > -1) {
          accountings.splice(ind, 1);
        }
      }
    }
  }

  // Lets remove the applied code from the state
  if (accountingToRemove) {
    const codeId = accountingToRemove.promoCodeId;
    const codeUuid =
      accountingToRemove.promoCodeUuid || accountingToRemove.promocode;
    const completedAccountingsFromBackend = updatedBooking.accountings.filter(
      (acc) =>
        acc.state === AccountingState.Completed &&
        (acc.promoCodeId === codeId ||
          acc.promocode === codeUuid ||
          acc.promoCodeUuid === codeUuid) &&
        !acc.id
    );
    if (
      completedAccountingsFromBackend &&
      completedAccountingsFromBackend.length
    ) {
      // we don't remove the promocode from the booking module
    } else {
      const bookingUuid = accountingToRemove.bookingUuid;
      BookingModule.removePromocode({
        bookingUuid: String(bookingUuid),
        promocodeUuid: String(accountingToRemove.promoCodeUuid),
      });
    }
  }

  BookingModule.updateBooking(updatedBooking);
  BookingModule.setSplitPaymentAmount({
    BookingUUID: BookingModule.selectedBookingUUID,
    splitAmount: restToPay.value / 100,
  });
}

/**
 * Pull and store promocodeCampaigns linked to the selected POS config
 */
export async function pullAndStorePromocodeCampaigns(signal?: AbortSignal) {
  const posConfigId = POSConfigModule.selectedConfig?.id;
  if (!posConfigId) {
    return;
  }

  LogService.info(
    `Pulling and storing promocode campaigns linked to the selected POS config: ${posConfigId}`
  );

  const allCampaigns: IPromocodeCampaign[] = [];
  const limit = 100;
  let skip = 0;
  // eslint-disable-next-line
	while (true) {
    if (signal?.aborted) break;
    const campaings = await getPromocodeCampaginsByConfigId(posConfigId, {
      skip,
      limit,
    });
    if (!campaings || !campaings.length) {
      break;
    }
    // pull the pictures of the campaigns
    const ids = campaings
      .filter((c) => c?.PromoCodeCampaignRead?.id)
      .map((c) => Number(c?.PromoCodeCampaignRead?.id));
    const pictures = await getSmeetzPictures(
      SmeetzPictureEntityTypes.PromoCode,
      ids
    );

    skip += campaings.length;

    const now = dayjs();
    for (const campaign of campaings) {
      const c = campaign?.PromoCodeCampaignRead;
      const endDateTime = c?.endDateTime;
      if (!c || c.cancelled === 1 || dayjs(endDateTime).isBefore(now)) continue;
      const campaignPicture = pictures.find((p) => p?.entityId === c.id);
      const picture = campaignPicture
        ? createCroppedImageproxyUrl({
            name: campaignPicture?.name,
            table: SmeetzPictureEntityTables.PromoCode,
            metaData: campaignPicture?.metaData,
          })
        : "";
      allCampaigns.push({
        id: Number(c?.id),
        posConfigId: Number(campaign?.posConfigId),
        name: String(c?.name),
        startDateTime: String(c?.startDateTime),
        endDateTime: String(c?.endDateTime),
        codes: c?.codes?.map((p) => String(p?.code || p?.codeUnique)) || [],
        isPinned: Boolean(campaign?.pinned),
        flatValue: Number(c?.flatValue),
        percentageValue: Number(c?.percentValue),
        picture: picture,
        position: campaign?.position ?? 25,
      });
    }
  }

  // Atomically replace the promocode campaigns table in Dexie
  // If this fails, the old data remains intact
  if (!signal?.aborted) {
    await atomicTableSwap(dexieDB.promocodeCampaigns as any, allCampaigns);
  }

  LogService.info(
    `Done pulling and storing promocode campaigns linked to the selected POS config: ${posConfigId}`
  );
}

/**
 * Removes pending payment/discount code accountings of currently applied codes, then recreates their accountings.
 * Used when PSS list is changed, to recalculate how promocodes are applied
 * @param booking a cloned copy of the booking to work on
 * @param options.updateGenericDiscounts set true to update generic discounts after removing all pending accountings
 */
export function removeAndReapplyPromocodes(
  originalBooking: IProductSlotBooking | null
) {
  if (!originalBooking) {
    return;
  }

  const booking = cloneDeep(originalBooking);

  // check if there are generic discounts pending or applied
  const hasGenericDiscount = hasAppliedGenericDiscountTracking.value;
  let hasPendingGenericDiscountAccounting = false;
  const genericDiscountAccIndex = (
    BookingModule.selectedBooking as IProductSlotBooking
  ).accountings.findIndex(
    (acc) =>
      acc.state === AccountingState.Pending &&
      !acc.refund &&
      acc.paymentMethod === PaymentMethods.METHODS_POS_DISCOUNT
  );
  if (genericDiscountAccIndex > -1) {
    hasPendingGenericDiscountAccounting = true;
  }

  /**
   * Step 1 – Remove all existing pending accountings for applied promo codes
   * This ensures we start from a clean state before reapplying them.
   */

  // Remove all pending payment code accountings
  removeAllPaymentCodePendingAccountings(booking);

  // Remove all pending discount code accountings
  removeAllDiscountCodePendingAccountings(booking);

  /**
   * Step 2 – If required, reapply generic discounts after removals
   */
  if (hasGenericDiscount || hasPendingGenericDiscountAccounting) {
    updateGenericDiscountAccountings(booking);
  }

  BookingModule.updateBooking(booking);

  /**
   * Step 3 – Recreate accountings for all applied promo codes
   */

  // --- 3.1 Handle Discount Codes ---
  const appliedDiscountCodes = BookingModule.appliedPromocodes.filter(
    (promo) =>
      ![
        PromocodeCampaignType.TYPE_VOUCHER,
        PromocodeCampaignType.TYPE_VOUCHER_SOLD,
        PromocodeCampaignType.TYPE_GIFT_CARD,
        PromocodeCampaignType.TYPE_GIFT_CARD_SOLD,
      ].includes(promo.promocode.promoCodeType)
  );

  // (Optional) If you want to apply oldest first: .sort((a, b) => a.createdAt - b.createdAt)
  for (const discountPromo of appliedDiscountCodes) {
    createDiscountCodeAccounting(booking, discountPromo);

    const generatedValue = getSumOfCodeGeneratedAccountingsValues(
      discountPromo.promocode.code
    );

    // If the code did not generate any accounting, remove it
    if (!generatedValue) {
      BookingModule.removePromocode({
        bookingUuid: String(booking.uuid),
        promocodeUuid: String(discountPromo.promocode.code),
      });
    }
  }

  // --- 3.2 Handle Payment Codes (Vouchers, Gift Cards) ---
  const appliedPaymentCodes = BookingModule.appliedPromocodes.filter((promo) =>
    [
      PromocodeCampaignType.TYPE_VOUCHER,
      PromocodeCampaignType.TYPE_VOUCHER_SOLD,
      PromocodeCampaignType.TYPE_GIFT_CARD,
      PromocodeCampaignType.TYPE_GIFT_CARD_SOLD,
    ].includes(promo.promocode.promoCodeType)
  );

  for (const paymentPromo of appliedPaymentCodes) {
    createPaymentCodeAccounting(booking, paymentPromo);

    const generatedValue = getSumOfCodeGeneratedAccountingsValues(
      paymentPromo.promocode.code
    );

    if (!generatedValue) {
      BookingModule.removePromocode({
        bookingUuid: String(booking.uuid),
        promocodeUuid: String(paymentPromo.promocode.code),
      });
    }
  }

  /**
   * Step 4 – Save the updated booking state with all promo codes reapplied
   */
  BookingModule.updateBooking(booking);
}
